import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getCurrentUser } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import type {
  Plybook,
  PlybookPly,
  PlybookFormData,
  PlyFormData,
  PlybookHistory,
  PlybookExecution,
  PlyConfirmation,
  PlybookZone,
  PlybookZonePosition,
} from '@/types/plybook';

// ============== QUERIES ==============

export function usePlybooks(filters?: {
  moldId?: string;
  customerId?: string;
  projectId?: string;
  status?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: ['plybooks', filters],
    queryFn: async () => {
      let query = api
        .from('plybooks')
        .select(`
          *,
          customer:customers(id, name),
          project:projects(id, name, reference_code),
          mold:molds(id, name, code),
          created_by_profile:profiles!plybooks_created_by_fkey(id, full_name)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (filters?.customerId) {
        query = query.eq('customer_id', filters.customerId);
      }
      if (filters?.projectId) {
        query = query.eq('project_id', filters.projectId);
      }
      if (filters?.moldId) {
        query = query.eq('mold_id', filters.moldId);
      }
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.search) {
        query = query.or([`name.ilike.%${filters.search}%`, `code.ilike.%${filters.search}%`]);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Plybook[];
    },
  });
}

export function usePlybook(id: string | undefined) {
  return useQuery({
    queryKey: ['plybook', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await api
        .from('plybooks')
        .select(`
          *,
          customer:customers(id, name),
          project:projects(id, name, reference_code),
          mold:molds(id, name, code),
          approved_by_profile:profiles!plybooks_approved_by_fkey(id, full_name),
          created_by_profile:profiles!plybooks_created_by_fkey(id, full_name),
          plies:plybook_plies(
            *,
            material:material_inventory(id, name, category)
          )
        `)
        .eq('id', id)
        .single();

      // DEBUG - remover después
      console.log('DEBUG usePlybook:', { id, data, error, pliesCount: data?.plies?.length });

      if (error) throw error;

      // Sort plies by ply_number
      if (data?.plies) {
        data.plies.sort((a: PlybookPly, b: PlybookPly) => a.ply_number - b.ply_number);
      }

      return data as Plybook;
    },
    enabled: !!id,
  });
}

export function usePlybookPlies(plybookId: string | undefined) {
  return useQuery({
    queryKey: ['plybook-plies', plybookId],
    queryFn: async () => {
      if (!plybookId) return [];

      const { data, error } = await api
        .from('plybook_plies')
        .select(`
          *,
          material:material_inventory(id, name, category)
        `)
        .eq('plybook_id', plybookId)
        .order('ply_number', { ascending: true });

      if (error) throw error;
      return data as PlybookPly[];
    },
    enabled: !!plybookId,
  });
}

export function usePlybookHistory(plybookId: string | undefined) {
  return useQuery({
    queryKey: ['plybook-history', plybookId],
    queryFn: async () => {
      if (!plybookId) return [];

      const { data, error } = await api
        .from('plybook_history')
        .select(`
          *,
          changed_by_profile:profiles!plybook_history_changed_by_fkey(id, full_name)
        `)
        .eq('plybook_id', plybookId)
        .order('changed_at', { ascending: false });

      if (error) throw error;
      return data as PlybookHistory[];
    },
    enabled: !!plybookId,
  });
}

// ============== MUTATIONS ==============

// El código de plybook tiene constraint único en BD. Los plybooks "eliminados"
// solo se desactivan (is_active=false), así que siguen ocupando su código
// aunque no aparezcan en la lista.
function friendlyPlybookError(error: any): string {
  if (error?.code === '23505' || String(error?.message || '').includes('plybooks_code_key')) {
    return 'Ese código ya está en uso por otro plybook (puede estar archivado). Usa otro código, p.ej. añadiendo -V2.';
  }
  return error?.message || 'No se pudo guardar el plybook';
}

export function useCreatePlybook() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: PlybookFormData) => {
      const user = getCurrentUser();
      const { data: result, error } = await api
        .from('plybooks')
        .insert([{
          ...data,
          created_by: user?.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plybooks'] });
      toast({ title: 'Plybook creado', description: 'La hoja de laminado se creó correctamente' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al crear',
        description: friendlyPlybookError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useUpdatePlybook() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    // Acepta payload plano ({ id, ...campos }), que es como lo invoca
    // PlybookFormDialog; la firma anterior ({ id, data }) hacía que la
    // edición no guardara ningún campo.
    mutationFn: async ({ id, ...data }: { id: string } & Partial<PlybookFormData>) => {
      const { error } = await api
        .from('plybooks')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['plybooks'] });
      queryClient.invalidateQueries({ queryKey: ['plybook', id] });
      toast({ title: 'Plybook actualizado' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al actualizar',
        description: friendlyPlybookError(error),
        variant: 'destructive',
      });
    },
  });
}

export function useDeletePlybook() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      // Obtener el código actual para liberar el código original
      const { data: plybook } = await api.from("plybooks").select("code").eq("id", id).single();
      const archiveCode = plybook?.code ? `${plybook.code}_DEL_${Date.now()}` : null;
      const { error } = await api
        .from('plybooks')
        .update({ is_active: false, code: archiveCode, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plybooks'] });
      toast({ title: 'Plybook eliminado' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al eliminar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useApprovePlybook() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      // Obtener el código actual para liberar el código original
      const { data: plybook } = await api.from("plybooks").select("code").eq("id", id).single();
      const archiveCode = plybook?.code ? `${plybook.code}_DEL_${Date.now()}` : null;
      const user = getCurrentUser();
      const { data, error } = await api.rpc('approve_plybook', {
        p_plybook_id: id,
        p_user_id: user?.id,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['plybooks'] });
      queryClient.invalidateQueries({ queryKey: ['plybook', id] });
      toast({ title: 'Plybook aprobado', description: 'La hoja de laminado fue aprobada' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al aprobar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// ============== PLY MUTATIONS ==============

export function useAddPly() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ plybookId, data }: { plybookId: string; data: PlyFormData }) => {
      // Convert empty strings to null for UUID fields
      const cleanData = {
        ...data,
        material_id: data.material_id || null,
      };

      const { data: result, error } = await api
        .from('plybook_plies')
        .insert([{
          plybook_id: plybookId,
          ...cleanData,
        }])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (_, { plybookId }) => {
      queryClient.invalidateQueries({ queryKey: ['plybook', plybookId] });
      queryClient.invalidateQueries({ queryKey: ['plybook-plies', plybookId] });
      toast({ title: 'Capa agregada' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al agregar capa',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdatePly() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      plybookId,
      data,
    }: {
      id: string;
      plybookId: string;
      data: Partial<PlyFormData>;
    }) => {
      // Convert empty strings to null for UUID fields
      const cleanData = {
        ...data,
        material_id: data.material_id === '' ? null : data.material_id,
      };

      const { error } = await api
        .from('plybook_plies')
        .update({ ...cleanData, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, { plybookId }) => {
      queryClient.invalidateQueries({ queryKey: ['plybook', plybookId] });
      queryClient.invalidateQueries({ queryKey: ['plybook-plies', plybookId] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al actualizar capa',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeletePly() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, plybookId }: { id: string; plybookId: string }) => {
      const { error } = await api
        .from('plybook_plies')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, { plybookId }) => {
      queryClient.invalidateQueries({ queryKey: ['plybook', plybookId] });
      queryClient.invalidateQueries({ queryKey: ['plybook-plies', plybookId] });
      toast({ title: 'Capa eliminada' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al eliminar capa',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useReorderPlies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      plybookId,
      plies,
    }: {
      plybookId: string;
      plies: { id: string; ply_number: number }[];
    }) => {
      // Update each ply's number
      for (const ply of plies) {
        const { error } = await api
          .from('plybook_plies')
          .update({ ply_number: ply.ply_number })
          .eq('id', ply.id);

        if (error) throw error;
      }
    },
    onSuccess: (_, { plybookId }) => {
      queryClient.invalidateQueries({ queryKey: ['plybook', plybookId] });
      queryClient.invalidateQueries({ queryKey: ['plybook-plies', plybookId] });
    },
  });
}

export function useUpdatePlyPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      plybookId,
      position_x,
      position_y,
    }: {
      id: string;
      plybookId: string;
      position_x: number;
      position_y: number;
    }) => {
      const { error } = await api
        .from('plybook_plies')
        .update({ position_x, position_y, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, { plybookId }) => {
      queryClient.invalidateQueries({ queryKey: ['plybook', plybookId] });
      queryClient.invalidateQueries({ queryKey: ['plybook-plies', plybookId] });
    },
  });
}

// ============== EXECUTION QUERIES ==============

export function usePlybookExecution(workOrderId: string | undefined) {
  return useQuery({
    queryKey: ['plybook-execution', workOrderId],
    queryFn: async () => {
      if (!workOrderId) return null;

      const { data, error } = await api
        .from('plybook_executions')
        .select(`
          *,
          plybook:plybooks(
            *,
            plies:plybook_plies(*)
          ),
          operator:profiles!plybook_executions_operator_id_fkey(id, full_name),
          confirmations:plybook_ply_confirmations(
            *,
            confirmed_by_profile:profiles!plybook_ply_confirmations_confirmed_by_fkey(id, full_name),
            plotter_cut:plotter_cuts(id, package_code, batch_number)
          )
        `)
        .eq('work_order_id', workOrderId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingleRow();

      if (error) throw error;

      // Sort plies and confirmations
      if (data?.plybook?.plies) {
        data.plybook.plies.sort((a: any, b: any) => a.ply_number - b.ply_number);
      }
      if (data?.confirmations) {
        data.confirmations.sort((a: any, b: any) => a.ply_number - b.ply_number);
      }

      return data as PlybookExecution | null;
    },
    enabled: !!workOrderId,
  });
}

export function usePlybookExecutions(filters?: {
  status?: string;
  operatorId?: string;
}) {
  return useQuery({
    queryKey: ['plybook-executions', filters],
    queryFn: async () => {
      let query = api
        .from('plybook_executions')
        .select(`
          *,
          work_order:work_orders(id, order_number, product_name),
          plybook:plybooks(id, name, code, total_plies),
          operator:profiles!plybook_executions_operator_id_fkey(id, full_name)
        `)
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.operatorId) {
        query = query.eq('operator_id', filters.operatorId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PlybookExecution[];
    },
  });
}

// ============== EXECUTION MUTATIONS ==============

export function useStartPlybookExecution() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      workOrderId,
      plybookId,
      operatorId,
      processRecordId,
    }: {
      workOrderId: string;
      plybookId: string;
      operatorId: string;
      processRecordId?: string;
    }) => {
      const { data, error } = await api.rpc('start_plybook_execution', {
        p_work_order_id: workOrderId,
        p_plybook_id: plybookId,
        p_operator_id: operatorId,
        p_process_record_id: processRecordId || null,
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: (_, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: ['plybook-execution', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['plybook-executions'] });
      toast({ title: 'Laminado iniciado', description: 'Puedes comenzar a confirmar las capas' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al iniciar laminado',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useConfirmPly() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      executionId,
      plyId,
      plyNumber,
      operatorId,
      plotterCutId,
      notes,
      quantityUsed,
      durationSeconds,
      startedAt,
    }: {
      executionId: string;
      plyId: string;
      plyNumber: number;
      operatorId: string;
      plotterCutId?: string;
      notes?: string;
      quantityUsed?: number;
      durationSeconds?: number;
      startedAt?: string;
    }) => {
      // First try RPC, then update timing directly
      const { data, error } = await api.rpc('confirm_ply', {
        p_execution_id: executionId,
        p_ply_id: plyId,
        p_ply_number: plyNumber,
        p_operator_id: operatorId,
        p_plotter_cut_id: plotterCutId || null,
        p_notes: notes || null,
        p_quantity_used: quantityUsed || null,
      });

      if (error) throw error;

      // Update timing info directly if provided
      if (durationSeconds || startedAt) {
        const { error: updateError } = await api
          .from('plybook_ply_confirmations')
          .update({
            duration_seconds: durationSeconds || null,
            started_at: startedAt || null,
          })
          .eq('execution_id', executionId)
          .eq('ply_number', plyNumber);

        if (updateError) console.error('Error updating timing:', updateError);
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate plybook queries
      queryClient.invalidateQueries({ queryKey: ['plybook-execution'] });
      queryClient.invalidateQueries({ queryKey: ['plybook-executions'] });
      // Invalidate inventory queries (auto-consumed materials)
      queryClient.invalidateQueries({ queryKey: ['material-inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      queryClient.invalidateQueries({ queryKey: ['recent-movements'] });
      queryClient.invalidateQueries({ queryKey: ['low_stock_alerts'] });
      toast({ title: 'Capa confirmada', description: 'Material descontado del inventario' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error al confirmar capa',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function usePausePlybookExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (executionId: string) => {
      const { error } = await api
        .from('plybook_executions')
        .update({ status: 'paused', updated_at: new Date().toISOString() })
        .eq('id', executionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plybook-execution'] });
      queryClient.invalidateQueries({ queryKey: ['plybook-executions'] });
    },
  });
}

// ============== PLYBOOK SELECTOR FOR WORK ORDERS ==============

export function useApprovedPlybooks(filters?: {
  moldId?: string;
}) {
  return useQuery({
    queryKey: ['approved-plybooks', filters],
    queryFn: async () => {
      let query = api
        .from('plybooks')
        .select('id, name, code, total_plies, version, mold_id')
        .eq('status', 'approved')
        .eq('is_active', true)
        .order('name');

      if (filters?.moldId) {
        query = query.eq('mold_id', filters.moldId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

// ============== ZONE POSITIONS ==============

export function useZonePositions(plybookId: string) {
  return useQuery({
    queryKey: ['zone-positions', plybookId],
    queryFn: async () => {
      const { data, error } = await api
        .from('plybook_zone_positions')
        .select('*, zone:plybook_zones(*)')
        .eq('plybook_id', plybookId);
      if (error) throw error;
      return data as PlybookZonePosition[];
    },
    enabled: !!plybookId,
  });
}

export function useUpsertZonePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ plybookId, zoneId, position_x, position_y }: {
      plybookId: string;
      zoneId: string;
      position_x: number;
      position_y: number;
    }) => {
      const { error } = await api
        .from('plybook_zone_positions')
        .upsert({
          plybook_id: plybookId,
          zone_id: zoneId,
          position_x,
          position_y,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'plybook_id,zone_id' });
      if (error) throw error;
    },
    onSuccess: (_, { plybookId }) => {
      queryClient.invalidateQueries({ queryKey: ['zone-positions', plybookId] });
    },
  });
}

export function usePlybookZones() {
  return useQuery({
    queryKey: ['plybook-zones'],
    queryFn: async () => {
      const { data, error } = await api
        .from('plybook_zones')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as PlybookZone[];
    },
  });
}

export function useCreatePlybookZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ plybookId, name, color }: {
      plybookId: string;
      name: string;
      color: string;
    }) => {
      // Primero crear la zona en plybook_zones (o encontrar una existente con el mismo nombre)
      let zoneId: string;
      
      // Buscar si ya existe una zona con ese nombre para este plybook
      const { data: existingZone } = await api
        .from('plybook_zones')
        .select('id')
        .eq('name', name)
        .eq('plybook_id', plybookId)
        .maybeSingleRow();
      
      if (existingZone) {
        zoneId = existingZone.id;
      } else {
        // Crear nueva zona para este plybook
        const { data: newZone, error: zoneError } = await api
          .from('plybook_zones')
          .insert({
            name,
            color,
            plybook_id: plybookId,
            sort_order: 0,
            is_default: false,
            is_active: true,
          })
          .select('id')
          .single();
        
        if (zoneError) throw zoneError;
        zoneId = newZone.id;
      }

      // Crear la posición para este plybook
      const { error: posError } = await api
        .from('plybook_zone_positions')
        .insert({
          plybook_id: plybookId,
          zone_id: zoneId,
          position_x: 0,
          position_y: 0,
        });
      
      if (posError) throw posError;
    },
    onSuccess: (_, { plybookId }) => {
      queryClient.invalidateQueries({ queryKey: ['zone-positions', plybookId] });
    },
  });
}

export function useDeletePlybookZonePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (positionId: string) => {
      const { error } = await api
        .from('plybook_zone_positions')
        .delete()
        .eq('id', positionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zone-positions'] });
    },
  });
}

export function useInitializeDefaultZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (plybookId: string) => {
      // Verificar si ya hay zonas para este plybook
      const { data: existing } = await api
        .from('plybook_zone_positions')
        .select('id')
        .eq('plybook_id', plybookId)
        .limit(1);
      
      if (existing && existing.length > 0) return; // Ya hay zonas

      // Buscar o crear Zona 0
      let zoneId: string;
      const { data: zone0 } = await api
        .from('plybook_zones')
        .select('id')
        .eq('name', 'Zona 0')
        .maybeSingleRow();
      
      if (zone0) {
        zoneId = zone0.id;
      } else {
        const { data: newZone, error } = await api
          .from('plybook_zones')
          .insert({
            name: 'Zona 0',
            color: '#ec4899',
            sort_order: 0,
            is_default: true,
            is_active: true,
          })
          .select('id')
          .single();
        if (error) throw error;
        zoneId = newZone.id;
      }

      // Crear posición inicial
      await api
        .from('plybook_zone_positions')
        .insert({
          plybook_id: plybookId,
          zone_id: zoneId,
          position_x: 0,
          position_y: 0,
        });
    },
    onSuccess: (_, plybookId) => {
      queryClient.invalidateQueries({ queryKey: ['zone-positions', plybookId] });
    },
  });
}
